import React, { useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { getLearnerByUrlTokenAsync } from "../../Services/ClientService";
import Card from "../../Shared/Components/Card/Card";
import PrimaryButton from "../../Shared/Components/PrimaryButton/PrimaryButton";
import Table from "../../Shared/Components/Table";
import Header from "./../../Shared/Components/Header/Header";
import { deleteUserAsync, updateUserAsync } from "../../Services/UserService";
import { showToast } from "../../Infrastructure/Notifications/ToastrConfig";
import {
  resendActivationEmailAsync,
  sendReminderAsync,
} from "../../Services/AccountService";
import Modal from "../../Shared/Components/Modal/Modal";
import UserCourseAssignments from "./Components/UserCourseAssignments";
import { validEmailRegex } from "./../../Infrastructure/Config/Regexes";
import { PASS_MARK } from "./../../Infrastructure/Utilities/LearnerProgressHelper";
import { formatDateTime } from "../../Infrastructure/Utilities/DateHelper";

const Learner = () => {
  const [learner, setLearner] = useState(null);
  const [courses, setCourses] = useState([]);
  const [editMode, setEditMode] = useState(false);
  const history = useHistory();
  const { urlToken } = useParams();
  const [errors, setErrors] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [sendingReminder, setSendingReminder] = useState(false);

  useEffect(() => {
    const getLearner = async () => {
      try {
        setIsLoading(true);
        let response = await getLearnerByUrlTokenAsync(urlToken);
        if (response && response.user) {
          setLearner(response.user);
          setCourses(response.courses || []);
        } else {
          showToast(false, "Learner not found");
          history.push("/manage-learners");
        }
      } catch (error) {
        console.error("Error fetching learner:", error);
        showToast(false, "Failed to load learner");
        history.push("/manage-learners");
      } finally {
        setIsLoading(false);
      }
    };
    getLearner();
  }, [urlToken, editMode, history]);

  const headings = () => {
    return (
      <>
        <th>Course title</th>
        <th>Type</th>
        <th>Progress</th>
        <th>Started</th>
        <th>Completed</th>
        <th>Assessment score</th>
        <th>Result</th>
        <th>No. of Restarts</th>
      </>
    );
  };

  const getScorePercentage = (courseEntry) => {
    const rawScore =
      courseEntry?.scorePercentage ??
      courseEntry?.ScorePercentage ??
      courseEntry?.course?.scorePercentage ??
      courseEntry?.course?.ScorePercentage;

    if (rawScore === null || rawScore === undefined || rawScore === "") {
      return null;
    }

    const numericScore = Number(rawScore);
    return Number.isNaN(numericScore) ? null : Math.round(numericScore);
  };

  const isCourseCompleted = (courseEntry) => {
    if (courseEntry?.lessonStatus === 4) {
      return true;
    }

    if (courseEntry?.completedDateTime) {
      return true;
    }

    const progress = Number(courseEntry?.progress);
    return !Number.isNaN(progress) && progress >= 100;
  };

  const rowData = () => {
    return (
      <>
        {courses.length > 0 ? (
          courses.map((c, i) => (
            <tr key={i}>
              <td>{c.course.title}</td>
              <td>
                {(c.hasAssessment ?? c.course?.hasAssessment)
                  ? "Assessment"
                  : "Mindfullness"}
              </td>
              <td>
                {isCourseCompleted(c) ? "100%" : `${Math.round(c.progress)}%`}
              </td>
              <td>{formatDateTime(c.startedDateTime)}</td>
              <td>{formatDateTime(c.completedDateTime)}</td>
              <td>
                {(c.hasAssessment ?? c.course?.hasAssessment)
                  ? getScorePercentage(c) !== null
                    ? `${getScorePercentage(c)}%`
                    : "-"
                  : "-"}
              </td>
              <td>
                {(c.hasAssessment ?? c.course?.hasAssessment) ? (
                  getStatusBadge(c)
                ) : (
                  <span className="badge badge-secondary">No assessment</span>
                )}
              </td>
              <td>{c.numerOfRestarts}</td>
            </tr>
          ))
        ) : (
          <tr>
            <td className="no-data" colSpan="8">
              No courses to display
            </td>
          </tr>
        )}
      </>
    );
  };

  const handleValidation = () => {
    let fields = learner;
    let errors = {};
    let formIsValid = true;

    //firstName
    if (!fields["firstName"]) {
      formIsValid = false;
      errors["firstName"] = "Please enter a first name";
    }

    //lastName
    if (!fields["lastName"]) {
      formIsValid = false;
      errors["lastName"] = "Please enter a last name";
    }

    //email
    if (!fields["email"]) {
      formIsValid = false;
      errors["email"] = "Please enter an email address";
    } else {
      if (!validEmailRegex.test(fields["email"])) {
        formIsValid = false;
        errors["email"] = "Please enter a valid email address";
      }
    }

    setErrors(errors);
    return formIsValid;
  };

  const updateUser = async () => {
    if (handleValidation()) {
      let respose = await updateUserAsync(learner);
      if (respose.success) {
        showToast(true, "User updated");
        setEditMode(false);
      } else {
        showToast(false, respose.errors[0].error);
      }
    }
  };

  const deleteUser = async () => {
    let data = {
      id: learner.id,
    };
    let respose = await deleteUserAsync(data);
    if (respose.success) {
      showToast(true, "User deleted");
      history.goBack();
    } else {
      showToast(false, respose.errors[0].error);
    }
  };

  const handleChange = (evt) => {
    setLearner({ ...learner, [evt.target.name]: evt.target.value });
  };

  const resendConfirmationEmail = async () => {
    let data = {
      userId: learner.id,
    };
    let response = await resendActivationEmailAsync(data);
    if (response.success) {
      showToast(true, "Email sent");
    } else {
      showToast(false, response.errors[0].error);
    }
  };

  const getStatusBadge = (courseEntry) => {
    const status = courseEntry?.lessonStatus;
    const score = getScorePercentage(courseEntry);

    // A recorded score means the assessment was taken - Result can never be "Not taken" when
    // a score exists, independent of whether the rest of the course is complete yet.
    if (score !== null) {
      return score >= PASS_MARK ? (
        <span className="badge badge-success">Passed / Completed</span>
      ) : (
        <span className="badge badge-danger">Failed</span>
      );
    } else if (status === 4 || status === 2) {
      return <span className="badge badge-success">Passed / Completed</span>;
    } else if (status === 3) {
      return <span className="badge badge-danger">Failed</span>;
    } else {
      return <span className="badge badge-warning">Assessment Not taken</span>;
    }
  };

  const hasIncompleteCourses = courses.some(
    (course) => !isCourseCompleted(course),
  );

  const sendLearnerReminder = async () => {
    if (!learner?.id) {
      return;
    }

    if (!hasIncompleteCourses) {
      return;
    }

    setSendingReminder(true);
    try {
      const response = await sendReminderAsync({ userIds: [learner.id] });
      if (response?.success) {
        showToast(true, "Reminder email sent");
      } else {
        showToast(
          false,
          response?.errors?.[0]?.error || "Failed to send reminder email",
        );
      }
    } catch (error) {
      showToast(false, "Failed to send reminder email");
    } finally {
      setSendingReminder(false);
    }
  };

  return (
    <>
      {isLoading ? (
        <div className="container-fluid content">
          <div style={{ textAlign: "center", padding: "60px" }}>
            <div className="spinner-border text-primary" role="status">
              <span className="sr-only">Loading...</span>
            </div>
          </div>
        </div>
      ) : !learner ? (
        <div className="container-fluid content">
          <div style={{ textAlign: "center", padding: "60px" }}>
            <h3>Learner not found</h3>
          </div>
        </div>
      ) : (
        <>
          <Modal
            buttonFunc={deleteUser}
            modalId={"delete"}
            bodyText="Warning! Deleting users is ireversable! Are you sure you want to delete this user?"
            title={`Delete ${learner.firstName} ${learner.lastName}`}
            buttonText="Delete"
          />
          <Header
            heading="Learner"
            subheading="Learner Information"
            icon="layout"
          />
          <div className="container-fluid content">
            <Card
              classes="mb-5"
              title={learner.firstName + " " + learner.lastName}
            >
              <div className="form-row">
                <div className="form-group col-md-6">
                  <label>First Name</label>
                  <input
                    type="text"
                    onChange={handleChange}
                    value={learner.firstName}
                    className="form-control"
                    name="firstName"
                    disabled={!editMode}
                  />
                  <small className="errorMessage">{errors.firstName}</small>
                </div>
                <div className="form-group col-md-6">
                  <label>Last Name</label>
                  <input
                    type="text"
                    onChange={handleChange}
                    value={learner.lastName}
                    className="form-control"
                    name="lastName"
                    disabled={!editMode}
                  />
                  <small className="errorMessage">{errors.lastName}</small>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group col-md-6">
                  <label>Email</label>
                  <input
                    onChange={handleChange}
                    type="text"
                    value={learner.email}
                    className="form-control"
                    name="email"
                    disabled={!editMode}
                  />
                  <small className="errorMessage">{errors.email}</small>
                </div>
                <div className="form-group col-md-6">
                  <label>Group</label>
                  <input
                    type="text"
                    value={learner.groupName || "No group assigned"}
                    className="form-control"
                    disabled={true}
                  />
                </div>
                <div className="form-group col-md-6">
                  <label>Registered</label>
                  <input
                    type="text"
                    value={formatDateTime(learner.createdDateTime)}
                    className="form-control"
                    disabled={true}
                  />
                </div>
              </div>
              {!editMode ? (
                <PrimaryButton
                  func={() => {
                    setEditMode(true);
                  }}
                  text="Edit"
                  classes="float-right mt-2"
                />
              ) : (
                <>
                  <PrimaryButton
                    func={updateUser}
                    text="Save"
                    classes="float-right mt-2"
                  />
                  <PrimaryButton
                    func={() => {
                      setEditMode(false);
                      setErrors([]);
                    }}
                    text="Cancel"
                    classes="mr-3 float-right mt-2"
                  />
                </>
              )}
              {!editMode ? (
                <>
                  {!learner.emailConfirmed && (
                    <PrimaryButton
                      classes="mt-2"
                      text="Resend confirmation"
                      func={resendConfirmationEmail}
                    />
                  )}
                  <PrimaryButton
                    classes="mt-2 ml-2"
                    text={
                      sendingReminder ? "Sending Reminder..." : "Send Reminder"
                    }
                    func={sendLearnerReminder}
                    disabled={!hasIncompleteCourses || sendingReminder}
                  />
                </>
              ) : (
                <button
                  data-toggle="modal"
                  data-target={`#delete`}
                  className="btn btn-danger mt-2"
                >
                  Delete
                </button>
              )}
            </Card>

            <Card title="Course Assignments">
              <UserCourseAssignments
                userId={learner.id}
                userName={`${learner.firstName} ${learner.lastName}`}
              />
            </Card>

            <Card title="Courses">
              <div className="row">
                <div className="col-12">
                  <Table headings={headings} rowData={rowData} />
                </div>
              </div>
            </Card>
          </div>
        </>
      )}
    </>
  );
};

export default Learner;
